/**
 * GotraDirectory — IFT Gotra & Caste Explainer
 * Route: /indian-family-tree/gotra
 *
 * Searchable directory of Hindu gotras with etymology, lineage (pravar),
 * Veda association, and regional distribution.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, BookOpen, ChevronRight, Users, Globe, Sparkles, X,
  ScrollText, TreePine, Star,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const CREAM = "#FBF7F0";
const AMBER = "#C79A3F";
const SAFFRON = "#C2410C";
const DARK = "#1A1310";

const VEDA_COLORS = {
  Rigveda:    { bg: "#FEF3C7", text: "#92400E", ring: "#FDE68A" },
  Yajurveda:  { bg: "#EDE9FE", text: "#4C1D95", ring: "#C4B5FD" },
  Samaveda:   { bg: "#D1FAE5", text: "#065F46", ring: "#6EE7B7" },
  Atharvaveda:{ bg: "#FCE7F3", text: "#831843", ring: "#FBCFE8" },
};
function vedaStyle(veda) {
  for (const [key, val] of Object.entries(VEDA_COLORS)) {
    if (veda?.includes(key.replace("veda", ""))) return val;
  }
  return { bg: "#F1F5F9", text: "#475569", ring: "#CBD5E1" };
}

function PravarChain({ pravar = [] }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs mt-2" data-testid="pravar-chain">
      {pravar.map((r, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <ChevronRight className="w-3 h-3 opacity-40" />}
          <span className="rounded-full px-2 py-0.5 font-medium"
                style={{ background: "#F0E8D5", color: SAFFRON }}>
            {r}
          </span>
        </span>
      ))}
    </div>
  );
}

function GotraDetailPanel({ gotra, onClose }) {
  if (!gotra) return null;
  const vs = vedaStyle(gotra.veda);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
         data-testid="gotra-detail-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border bg-white shadow-2xl"
           style={{ borderColor: AMBER }}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b" style={{ borderColor: "#E9D8B4", background: CREAM }}>
          <div>
            <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: SAFFRON }}>
              IFT · Gotra Encyclopedia
            </span>
            <h2 className="font-serif text-3xl font-bold mt-1" style={{ color: DARK }}
                data-testid="gotra-detail-name">
              {gotra.gotra}
            </h2>
            {gotra.aliases?.length > 0 && (
              <div className="text-sm opacity-60 mt-1">Also: {gotra.aliases.join(" · ")}</div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 transition"
                  data-testid="gotra-detail-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Veda + pravar count */}
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full px-3 py-1 text-xs font-semibold ring-1"
                  style={{ background: vs.bg, color: vs.text, "--tw-ring-color": vs.ring }}
                  data-testid="gotra-detail-veda">
              {gotra.veda}
            </span>
            <span className="rounded-full px-3 py-1 text-xs font-semibold bg-slate-100 text-slate-700">
              {gotra.pravar_count}-Rishi Pravar
            </span>
            {gotra.castes?.map(c => (
              <span key={c} className="rounded-full px-3 py-1 text-xs bg-orange-50 text-orange-800">
                {c}
              </span>
            ))}
          </div>

          {/* Etymology */}
          <div data-testid="gotra-detail-etymology">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: SAFFRON }}>
              Etymology
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "#3E2A0A" }}>{gotra.etymology}</p>
          </div>

          {/* Lineage / Pravar */}
          <div data-testid="gotra-detail-pravar">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: SAFFRON }}>
              Pravar (Lineage Chain)
            </div>
            <p className="text-xs opacity-60 mb-2">
              The pravar recited during Vedic rituals — ancestors invoked to establish gotra identity.
            </p>
            <PravarChain pravar={gotra.pravar} />
          </div>

          {/* Founder */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: SAFFRON }}>
                Founder Rishi
              </div>
              <p className="text-sm font-semibold" style={{ color: DARK }}>{gotra.founder_rishi}</p>
              <p className="text-xs opacity-60 mt-0.5">{gotra.parent_gotra}</p>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: SAFFRON }}>
                Regional Distribution
              </div>
              <p className="text-xs" style={{ color: "#3E2A0A" }}>{gotra.region?.join(", ")}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: SAFFRON }}>
              About this Gotra
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "#3E2A0A" }}>{gotra.description}</p>
          </div>

          {/* Notable facts */}
          {gotra.notable_facts?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: SAFFRON }}>
                Notable Facts
              </div>
              <ul className="space-y-2">
                {gotra.notable_facts.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm" style={{ color: "#3E2A0A" }}>
                    <Star className="w-3.5 h-3.5 flex-none mt-0.5" style={{ color: AMBER }} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Language variants */}
          {gotra.language_variants && Object.keys(gotra.language_variants).length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: SAFFRON }}>
                Name in Other Languages
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(gotra.language_variants).map(([lang, name]) => (
                  <span key={lang} className="rounded-lg border px-3 py-1.5 text-xs"
                        style={{ borderColor: "#E9D8B4", background: CREAM }}>
                    <span className="opacity-50">{lang}:</span>{" "}
                    <span className="font-semibold">{name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Related gotras */}
          {gotra.related_gotras?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: SAFFRON }}>
                Related Gotras
              </div>
              <div className="flex flex-wrap gap-2">
                {gotra.related_gotras.map(g => (
                  <span key={g} className="rounded-full border px-3 py-1 text-xs font-medium hover:opacity-80 cursor-default"
                        style={{ borderColor: AMBER, color: SAFFRON, background: "#FEF9F0" }}>
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GotraCard({ gotra, onSelect }) {
  const vs = vedaStyle(gotra.veda);
  return (
    <button
      type="button"
      onClick={() => onSelect(gotra)}
      data-testid={`gotra-card-${gotra.slug}`}
      className="w-full text-left rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2"
      style={{ borderColor: "#E9D8B4" }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-serif text-xl font-bold" style={{ color: DARK }}>{gotra.gotra}</h3>
        <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ring-1"
              style={{ background: vs.bg, color: vs.text }}>
          {gotra.veda}
        </span>
      </div>

      {gotra.aliases?.length > 0 && (
        <div className="text-xs opacity-50 mb-2">{gotra.aliases.join(" · ")}</div>
      )}

      <p className="text-xs leading-relaxed line-clamp-3 mb-3" style={{ color: "#5A3E2B" }}>
        {gotra.etymology}
      </p>

      <PravarChain pravar={gotra.pravar?.slice(0, 3)} />

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] opacity-60">
        <span className="flex items-center gap-1">
          <ScrollText className="w-3 h-3" /> {gotra.pravar_count}-Rishi
        </span>
        <span className="flex items-center gap-1">
          <Globe className="w-3 h-3" /> {gotra.region?.slice(0, 2).join(", ")}
        </span>
        <span className="ml-auto flex items-center gap-1 font-medium" style={{ color: SAFFRON }}>
          Details <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
}

export default function GotraDirectory() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const q = params.get("q") || "";
  const veda = params.get("veda") || "";

  useEffect(() => {
    document.title = "Gotra Directory — Indian Family Tree";
    // Load stats once
    axios.get(`${API}/api/ift/gotra/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/ift/gotra`, {
          params: { q: q || undefined, veda: veda || undefined, limit: 50, offset: 0 },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, veda]);

  function setParam(key, val) {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  }

  const VEDAS = ["Rigveda", "Yajurveda", "Samaveda", "Atharvaveda"];

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="gotra-directory">
      <SeoHead
        title="Gotra Directory — Indian Family Tree"
        description="Searchable directory of Hindu gotras with etymology, lineage (pravar), Veda association, and regional distribution. Explore your ancestral gotra."
        canonical="https://foodpulse.net/indian-family-tree/gotra"
      />

      {selected && <GotraDetailPanel gotra={selected} onClose={() => setSelected(null)} />}

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb"
           className="max-w-6xl mx-auto px-4 pt-4 flex items-center gap-1 text-xs opacity-60"
           data-testid="gotra-breadcrumb">
        <Link to="/indian-family-tree" className="hover:underline" style={{ color: SAFFRON }}>
          Indian Family Tree
        </Link>
        <ChevronRight className="w-3 h-3 opacity-50 mx-0.5" aria-hidden="true" />
        <span aria-current="page">Gotra Directory</span>
      </nav>

      {/* Hero */}
      <section style={{ background: "#F5EDD8" }} className="border-b mt-4" data-testid="gotra-hero">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <div className="text-[10px] sm:text-xs font-mono uppercase tracking-widest mb-4"
               style={{ color: "#A0522D" }}>
            — Indian Family Tree · Ancestral Lineage
          </div>
          <h1 className="font-serif text-[44px] sm:text-[60px] lg:text-[72px] font-bold leading-[1.05] tracking-tight mb-4"
              style={{ color: DARK }}>
            Gotra Directory.{" "}
            <em style={{ color: SAFFRON, fontStyle: "italic" }}>Know your lineage.</em>
          </h1>
          <p className="text-sm sm:text-base max-w-2xl" style={{ color: "#4B3A20" }}>
            Your gotra is your ancient clan identity — a lineage traced back to a founding Vedic sage
            (rishi). Explore the etymology, pravar, Veda, and regional roots of each gotra.
          </p>

          {/* Stats strip */}
          {stats && (
            <div className="mt-6 flex flex-wrap gap-4 text-sm" data-testid="gotra-stats">
              <span className="flex items-center gap-2 rounded-full px-4 py-1.5 bg-white border"
                    style={{ borderColor: AMBER, color: "#5A3E2B" }}>
                <BookOpen className="w-4 h-4" style={{ color: SAFFRON }} />
                {stats.total} gotras documented
              </span>
              {stats.by_veda?.map(v => (
                <span key={v.veda} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
                      style={vedaStyle(v.veda)}>
                  {v.veda}: {v.count}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What is a Gotra — explainer */}
      <section className="border-b" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-6 md:grid-cols-3">
          <div className="flex gap-3">
            <TreePine className="w-5 h-5 flex-none mt-0.5" style={{ color: SAFFRON }} />
            <div>
              <div className="font-semibold text-sm mb-1" style={{ color: DARK }}>What is a Gotra?</div>
              <p className="text-xs leading-relaxed" style={{ color: "#5A3E2B" }}>
                A gotra is an exogamous patrilineal lineage tracing descent from a Vedic sage. All males
                in a family share the same gotra; daughters adopt their husband's gotra after marriage.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <ScrollText className="w-5 h-5 flex-none mt-0.5" style={{ color: SAFFRON }} />
            <div>
              <div className="font-semibold text-sm mb-1" style={{ color: DARK }}>What is a Pravar?</div>
              <p className="text-xs leading-relaxed" style={{ color: "#5A3E2B" }}>
                The pravar is a chain of 3 or 5 ancestral rishis recited during Vedic ceremonies (Yajna)
                to invoke your lineage. People sharing a pravar cannot marry — it is the ultimate
                genealogical identity marker.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Users className="w-5 h-5 flex-none mt-0.5" style={{ color: SAFFRON }} />
            <div>
              <div className="font-semibold text-sm mb-1" style={{ color: DARK }}>Gotras and Marriage</div>
              <p className="text-xs leading-relaxed" style={{ color: "#5A3E2B" }}>
                Marriage within the same gotra (sagotra) is traditionally prohibited in Hinduism because
                all members are considered siblings descended from the same ancestor. This genetic
                wisdom predates modern understanding of consanguinity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Search + Veda filter */}
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2 min-w-[260px] flex-1 max-w-sm"
               style={{ borderColor: AMBER }}>
            <Search className="w-4 h-4 opacity-50" />
            <input
              type="search"
              value={q}
              onChange={e => setParam("q", e.target.value)}
              placeholder="Search gotra, rishi, region…"
              className="w-full bg-transparent outline-none text-sm"
              aria-label="Search gotras"
              data-testid="gotra-search"
            />
            {q && (
              <button onClick={() => setParam("q", "")} aria-label="Clear search">
                <X className="w-4 h-4 opacity-40 hover:opacity-80" />
              </button>
            )}
          </div>

          {/* Veda filter chips */}
          <div className="flex flex-wrap gap-2" data-testid="gotra-veda-filters">
            <button
              onClick={() => setParam("veda", "")}
              data-testid="gotra-filter-all"
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!veda ? "text-white" : "bg-white hover:bg-amber-50"}`}
              style={!veda ? { background: DARK, borderColor: DARK } : { borderColor: AMBER, color: "#3E2A0A" }}
            >
              All Vedas {stats ? `· ${stats.total}` : ""}
            </button>
            {VEDAS.map(v => {
              const vs = vedaStyle(v);
              const active = veda === v;
              const count = stats?.by_veda?.find(b => b.veda === v)?.count;
              return (
                <button
                  key={v}
                  onClick={() => setParam("veda", active ? "" : v)}
                  data-testid={`gotra-filter-${v.toLowerCase()}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "font-semibold" : ""}`}
                  style={active
                    ? { background: vs.bg, borderColor: vs.ring, color: vs.text }
                    : { background: "#fff", borderColor: AMBER, color: "#3E2A0A" }}
                >
                  {v} {count ? `· ${count}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Results */}
      <section className="max-w-6xl mx-auto px-4 py-8" aria-label="Gotra results">
        {loading ? (
          <div className="text-sm opacity-60 py-10 text-center" data-testid="gotra-loading">
            Loading gotra directory…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center bg-white"
               style={{ borderColor: AMBER }} data-testid="gotra-empty">
            <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: AMBER }} />
            <div className="font-serif text-lg mb-1">No gotras match your search.</div>
            <div className="text-sm opacity-60">Try a different name or clear the Veda filter.</div>
          </div>
        ) : (
          <>
            <div className="mb-4 text-xs opacity-50" data-testid="gotra-result-count">
              Showing {items.length} of {total} gotras
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gotra-grid">
              {items.map(g => (
                <GotraCard key={g.slug} gotra={g} onSelect={setSelected} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* CTA */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#F5EDD8" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 text-center">
          <h2 className="font-serif text-2xl font-bold mb-2" style={{ color: DARK }}>
            Don't see your gotra?
          </h2>
          <p className="text-sm mb-4" style={{ color: "#5A3E2B" }}>
            We're continuously expanding the directory. Submit a gotra for review and our team will
            document it with full etymology and lineage research.
          </p>
          <a href="mailto:ift@socialpulse.net"
             className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white text-sm font-semibold transition hover:opacity-90"
             style={{ background: SAFFRON }}
             data-testid="gotra-submit-cta">
            Submit a Gotra
          </a>
        </div>
      </section>
    </div>
  );
}
